<template>
    <div class="animated fadeIn">
        <div class="kgr-row kgr-text--rightAligned">
            <button
                v-if="isEditable"
                class="kgr-button kgr-button--accent"
                @click="openCreateDialog"
            >
                {{ $t('Neues Vereins-Konto anlegen') }}
            </button>
        </div>
        <table class="kgr-table kgr-table--block kgr-table--striped printable">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Kontoinhaber') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('IBAN') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('BIC') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions kgr-hidden-print"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td
                        colspan="4"
                        style="text-align: center;"
                    >
                        <kgr-loader />
                    </td>
                </tr>
                <tr v-else-if="bankAccounts.length === 0">
                    <td
                        colspan="4"
                        style="text-align: center;"
                    >
                        {{ $t('dataTable.noData') }}
                    </td>
                </tr>
                <template v-else>
                    <tr
                        v-for="bankAccount in bankAccounts"
                        :key="bankAccount.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            {{ bankAccount.owner }}
                            <span
                                v-if="bankAccount.id === sepaBankAccountId"
                                class="kgr-appliedFilter accounting-sepa-label"
                                style="cursor: default;"
                            >
                                <span class="kgr-appliedFilter__label">
                                    SEPA
                                </span>
                            </span>
                        </td>
                        <td class="kgr-table__cell">
                            {{ bankAccount.iban }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ bankAccount.bic }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--actions kgr-hidden-print">
                            <button
                                class="kgr-table__action"
                                @click="deleteBankAccount(bankAccount)"
                            >
                                <kgr-icon icon-class="delete" />
                            </button>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        <hr class="kgr-separator--light" />
        <div class="kgr-row">
            <h2 class="kgr-headline kgr-headline--h2">
                {{ $t('SEPA-Konto') }}
            </h2>
        </div>
        <div
            class="kgr-alert kgr-alert--info kgr-row"
        >
            {{ $t('Wähle hier das SEPA-Konto deines Vereins aus.') }}
        </div>

        <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--top kgr-row">
            <div class="kgr-postfix__fluid">
                <kgr-generic-select
                    v-model="sepaBankAccount"
                    :options="items"
                    :loading="loading"
                    display="text"
                />
            </div>
            <div class="kgr-postfix__fixed">
                <button
                    v-if="isEditable"
                    class="kgr-button kgr-button--accent"
                    @click="setSepaBankAccount"
                >
                    {{ $t('save') }}
                </button>
            </div>
        </div>
        <kgr-lightbox
            v-if="newBankAccount"
            :headline="$t('Bankkonto anlegen')"
            :value="showDialog"
            animated
            @post-input="closeModal"
        >
            <div style="min-width: 450px;">
                <kgr-form-row
                    :label="$t('Kontoinhaber')"
                    :error="error"
                    required
                    field-name="owner"
                >
                    <kgr-text-input v-model="newBankAccount.owner" />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('IBAN')"
                    :error="error"
                    required
                    field-name="iban"
                >
                    <kgr-text-input v-model="newBankAccount.iban" />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('BIC')"
                    :error="error"
                    field-name="bic"
                >
                    <kgr-text-input v-model="newBankAccount.bic" />
                </kgr-form-row>
                <hr class="kgr-separator--light" />
                <div class="kgr-row">
                    <button
                        class="kgr-button"
                        @click="closeModal"
                    >
                        {{ $t('cancel') }}
                    </button>
                    <button
                        class="kgr-button kgr-button--accent"
                        @click="createBankAccount"
                    >
                        {{ $t('save') }}
                    </button>
                </div>
            </div>
        </kgr-lightbox>
    </div>
</template>

<script>
import ClubRole from '../../../js/dto/ClubRole.js';
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import GenericSelect from '../../../components/common/form/select/GenericSelect.vue';
import Icon from '../../../components/common/icon/Icon.vue';
import Lightbox from '../../../components/common/modal/Lightbox.vue';
import Loading from '../../../components/common/Loading.vue';
import TextInput from '../../../components/common/form/input/TextInput.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-icon': Icon,
        'kgr-loader': Loading,
        'kgr-lightbox': Lightbox,
        'kgr-form-row': FormRow,
        'kgr-text-input': TextInput,
        'kgr-generic-select': GenericSelect,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        }
    },
    data() {
        return {
            loading: false,
            error: new Error(),
            showDialog: false,
            bankAccounts: [],
            newBankAccount: null,
            sepaBankAccount: null,
            sepaBankAccountId: null,
            items: [],
            clubRoles: [],
        }
    },
    computed: {
        isEditable() {
            return this.clubRoles.length > 0 && (this.clubRoles.includes('admin') || this.clubRoles.includes('subadmin'));
        },
    },
    created() {
        this.loadClubRoles();
        this.loadData();
    },
    methods: {
        setSepaSelect() {
            this.sepaBankAccount = this.items.find((item) => {
                return item.value === this.sepaBankAccountId;
            });
        },
        loadData() {
            this.loading = true;

            Promise.all([
                axios.get('/club/clubs/' + this.clubId + '/club-bank-accounts'),
                axios.get(`/club/clubs/${this.clubId}`)
            ])
                .then(([bankAccountsResponse, clubResponse]) => {
                    this.bankAccounts = bankAccountsResponse.data.data;
                    this.items = this.bankAccounts.map(bankAccount => ({
                        text: bankAccount.owner + ', ' + bankAccount.iban + ', ' + bankAccount.bic,
                        value: bankAccount.id
                    }));

                    this.sepaBankAccountId = clubResponse.data.data.sepaBankAccountId;
                    this.setSepaSelect();
                })
                .catch((error) => {
                    console.error('Error loading data:', error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        createBankAccount() {
            this.loading = true;

            let payload = {
                'owner': this.newBankAccount.owner,
                'iban': this.newBankAccount.iban,
            };
            if (this.newBankAccount.bic) {
                payload.bic = this.newBankAccount.bic;
            }

            axios
                .post('/club/clubs/' + this.clubId + '/club-bank-accounts', payload)
                .then(() => {
                    this.closeModal();
                    this.loadData();
                })
                .catch((error) => {
                    this.error = Error.fromResponse(error.response)
                    this.$notify.error('saveChanges.error');
                    this.loading = false;
                });

            this.initNew();
        },
        deleteBankAccount(bankAccount) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst du das Konto wirklich löschen?'), () => {
                axios
                    .delete('/club/club-bank-accounts/' + bankAccount.id)
                    .then(() => this.loadData())
                    .catch(() => this.loading = false);
            });
        },
        initNew() {
            this.newBankAccount = {
                owner: '',
                iban: '',
                bic: '',
            };
        },
        openCreateDialog() {
            if (this.isEditable) {
                this.initNew();
            }
            this.showDialog = true;
        },
        closeModal() {
            this.showDialog = false;
        },
        setSepaBankAccount() {
            this.loading = true;
            axios
                .put('/club/clubs/' + this.clubId + '/sepa-bank-accounts', {'sepaBankAccountId': this.sepaBankAccount?.value})
                .then(() => {this.loadData()});
        },
        loadClubRoles() {
            axios.get('/club/clubs/' + this.clubId + '/club-roles/current-user')
                .then(response => {
                    const clubRoles = response.data.data.map(clubRole => ClubRole.fromData(clubRole)) ;
                    clubRoles.map(clubRole => {
                        this.clubRoles.push(clubRole.name);
                    });
                })
        },
    },
}
</script>
<style rel="stylesheet/scss" lang="scss" scoped>
.accounting-sepa-label {
    background-color: #4caf50;
    color: #fff;
}
</style>
